import { ShieldOff } from 'lucide-react';
import { foundation } from '../../../ui';
import useReducedMotion from '../../../hooks/ai/useReducedMotion';
import { ACTION_BEAT_ATTR } from '../../../ui/actionBeatMotion';

const f = foundation;
const fc = f.color;

const SIZE = 72;
const STROKE = 3;
const R = (SIZE - STROKE) / 2;
const RING_LEN = Math.ceil(2 * Math.PI * R);
// The drawing is always laid out on the 72 grid; `size` only scales the SVG's rendered box (viewBox), so the
// default (72) is byte-identical to before.
const CHECK_PATH = 'M24 37 L32 45 L48 29';
const CHECK_LEN = 35;

// Ring 0→100% (500ms, ease-out) → check draws (250ms) → pop 1→1.04→1.
// The RESTING style is the END state and each keyframe only supplies its
// `from` (fill-mode `backwards`), so a dropped/suspended animation on iOS
// still leaves a complete ring + check — nothing waits on `animationend`.
const EASE_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';
const KEYFRAMES = `@keyframes cvx-mfa-ring-draw { from { stroke-dashoffset: ${RING_LEN}; } }
@keyframes cvx-mfa-check-draw { from { stroke-dashoffset: ${CHECK_LEN}; } }
@keyframes cvx-mfa-mark-pop { 0% { transform: scale(1); } 50% { transform: scale(1.04); } 100% { transform: scale(1); } }
@keyframes cvx-mfa-rise { from { opacity: 0; transform: translateY(6px); } }`;

/** Title/body entrance timed to land after the check (plain CSS, no JS gate).
 *  Only valid next to an ANIMATED mark (`tone="success"` or `"brand"`) — its
 *  keyframes ship with it. */
export function mfaRiseStyle(reducedMotion, delayMs = 720) {
  return reducedMotion ? {} : { animation: `cvx-mfa-rise 280ms ${delayMs}ms ${EASE_OUT} backwards` };
}

/**
 * RD-SEC-015 — the centered done-screen mark. `tone="success"`: a green ring
 * that draws itself, then a check draws inside with a subtle pop.
 * `tone="neutral"`: a static grey ring with a `ShieldOff` glyph (turn-off —
 * nothing to celebrate). `tone="brand"` (2026-09-25 password flow): a blue
 * ring that draws itself around a glyph — "sent", not "done" (e.g. a reset
 * link emailed). `glyph` (a lucide component) overrides the neutral/brand
 * icon; the success tone always shows its check. Reduced motion → static.
 */
export default function MfaSuccessMark({ tone = 'success', glyph: GlyphProp = null, size = SIZE }) {
  const reducedMotion = useReducedMotion();
  const neutral = tone === 'neutral';
  const brand = tone === 'brand';
  const success = !neutral && !brand;
  const animate = !neutral && !reducedMotion;
  const ringColor = neutral ? fc.neutral.borderStrong : brand ? fc.brand.primary : fc.semantic.success;
  const fill = neutral ? fc.neutral.surfaceMuted : brand ? fc.brand.primaryMuted : fc.semantic.successSoft;
  const Glyph = success ? null : (GlyphProp || (neutral ? ShieldOff : null));

  return (
    <div
      aria-hidden
      data-testid="mfa-done-mark"
      data-animated={animate ? 'true' : 'false'}
      // A result screen is the end of the action: a touch pop-up resting under
      // the header glides back to the centre for it (useModalRestRelease).
      {...{ [ACTION_BEAT_ATTR]: 'done' }}
      style={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
        animation: animate ? `cvx-mfa-mark-pop 300ms 750ms ${EASE_OUT} both` : 'none',
      }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ display: 'block' }}>
        <circle cx={SIZE / 2} cy={SIZE / 2} r={R} fill={fill} />
        <circle
          data-testid="mfa-done-ring"
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          fill="none"
          stroke={ringColor}
          strokeWidth={STROKE}
          strokeLinecap="round"
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          style={{
            strokeDasharray: RING_LEN,
            strokeDashoffset: 0,
            animation: animate ? `cvx-mfa-ring-draw 500ms ${EASE_OUT} backwards` : 'none',
          }}
        />
        {success ? (
          <path
            data-testid="mfa-done-check"
            d={CHECK_PATH}
            fill="none"
            stroke={fc.semantic.success}
            strokeWidth={3.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: CHECK_LEN,
              strokeDashoffset: 0,
              animation: animate ? `cvx-mfa-check-draw 250ms 500ms ${EASE_OUT} backwards` : 'none',
            }}
          />
        ) : null}
      </svg>
      {Glyph ? (
        <span
          data-testid="mfa-done-glyph"
          style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: brand ? fc.brand.text : fc.neutral.textSecondary }}
        >
          <Glyph size={Math.round(30 * (size / SIZE))} strokeWidth={2} />
        </span>
      ) : null}
      {animate ? <style>{KEYFRAMES}</style> : null}
    </div>
  );
}
